import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight, GitBranch, FileCheck, Bot, ExternalLink } from 'lucide-react';

const Aureon = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 bg-gradient-to-br from-[#060D1B] via-[#0A1628] to-[#0d1f3c]">
        <div className="max-w-[1200px] mx-auto relative z-10">
          <div className="text-center">
            <div className="inline-block px-3 py-1 bg-[#B45309]/20 text-[#B45309] text-xs font-semibold uppercase tracking-wider rounded-full mb-4">
              Model Risk Management — A Cognifin Platform
            </div>
            <h1 className="text-h1 text-white mb-6">Aureon</h1>
            <p className="text-body-lg text-[#6B7280] max-w-3xl mx-auto mb-4">
              End-to-end Model Risk Management. Built for credit models. Designed to survive any audit.
            </p>
            <p className="text-[#6B7280] max-w-3xl mx-auto mb-8">
              Aureon is Cognifin's dedicated MRM platform — available as a standalone product at aureon-mrm.com. It brings AI-driven lifecycle governance to credit model validation, documentation, and examiner pack generation.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild 
                size="lg" 
                className="bg-[#B45309] hover:bg-[#B45309]/90 text-white font-semibold text-lg px-8 h-14 btn-hover-lift shadow-lg shadow-[#B45309]/30"
              >
                <a 
                  href="https://aureon-mrm.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2"
                >
                  Visit Aureon
                  <ExternalLink size={18} />
                </a>
              </Button>
              <Button 
                asChild 
                size="lg" 
                variant="outline" 
                className="border-2 border-white/30 text-white hover:bg-white hover:text-[#0A1628] text-lg px-8 h-14 font-semibold btn-hover-lift"
              >
                <Link to="/contact" className="inline-flex items-center gap-2">
                  Request a Demo
                  <ArrowRight size={18} />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Aureon Preview */}
      <section className="section-padding px-6 bg-white">
        <div className="max-w-[1200px] mx-auto">
          <div className="max-w-4xl mx-auto mb-10">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-4">
              <h2 className="text-h3 text-[#0A1628] text-center sm:text-left">Aureon in Action</h2>
              <Button
                asChild
                className="bg-[#1A56DB] hover:bg-[#1A56DB]/90 text-white font-semibold"
              >
                <Link to="/contact">Request a Demo</Link>
              </Button>
            </div>
            <p className="text-body-lg text-[#6B7280] leading-relaxed text-center sm:text-left">
              Explore the standalone Aureon experience directly within the page before diving into the platform capabilities.
            </p>
          </div>

          <div className="hidden lg:block rounded-2xl border border-[#D1D5DB] bg-white p-3 shadow-lg">
            <iframe
              src="/aureon.html"
              title="Aureon standalone experience"
              className="h-[720px] xl:h-[820px] w-full rounded-xl border-0"
            />
          </div>

          <Card className="lg:hidden bg-[#EFF6FF] border-[#D1D5DB] rounded-2xl shadow-sm">
            <CardContent className="p-8 text-center">
              <div className="inline-block px-3 py-1 bg-[#B45309]/10 text-[#B45309] text-xs font-semibold uppercase tracking-wider rounded-full mb-4">
                Responsive Preview
              </div>
              <h3 className="text-2xl font-bold text-[#0A1628] mb-4">Aureon Experience</h3>
              <p className="text-[#6B7280] leading-relaxed mb-6 max-w-2xl mx-auto">
                The full Aureon walkthrough is optimized for larger screens. On mobile and tablet, use the standalone experience or request a guided demo to explore the complete workflow.
              </p>
              <div className="grid sm:grid-cols-3 gap-4 mb-8 text-left">
                <div className="bg-white border border-[#D1D5DB] rounded-xl p-5">
                  <div className="text-sm font-semibold text-[#0A1628] mb-2">Portfolio Governance</div>
                  <p className="text-sm text-[#6B7280]">Track every model, review cycle, and control in one governed registry.</p>
                </div>
                <div className="bg-white border border-[#D1D5DB] rounded-xl p-5">
                  <div className="text-sm font-semibold text-[#0A1628] mb-2">Validation Workflow</div>
                  <p className="text-sm text-[#6B7280]">Run structured validation with audit-ready evidence and sign-off steps.</p>
                </div>
                <div className="bg-white border border-[#D1D5DB] rounded-xl p-5">
                  <div className="text-sm font-semibold text-[#0A1628] mb-2">Agentic Co-pilot</div>
                  <p className="text-sm text-[#6B7280]">Use AI assistance for documentation, gap detection, and governed answers.</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild className="bg-[#B45309] hover:bg-[#B45309]/90 text-white font-semibold">
                  <a href="https://aureon-mrm.com" target="_blank" rel="noopener noreferrer">
                    Open Standalone Aureon
                  </a>
                </Button>
                <Button asChild variant="outline" className="border-[#1A56DB] text-[#1A56DB] hover:bg-[#1A56DB] hover:text-white font-semibold">
                  <Link to="/contact">Request a Guided Demo</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Three Capability Tiles */}
      <section className="section-padding px-6 bg-[#EFF6FF]">
        <div className="max-w-[1200px] mx-auto">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <h2 className="text-h3 text-[#0A1628] mb-4">Core Capabilities</h2>
            <p className="text-body-lg text-[#6B7280] leading-relaxed">
              A tighter model risk workflow covering governance, documentation, and examiner readiness in one platform.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-white border-[#D1D5DB] rounded-xl shadow-sm hover:shadow-lg transition-shadow">
              <CardContent className="p-8">
                <div className="p-3 bg-[#B45309]/10 rounded-lg w-fit mb-4">
                  <GitBranch className="text-[#B45309]" size={28} />
                </div>
                <h3 className="text-xl font-bold text-[#0A1628] mb-3">End-to-End Lifecycle Governance</h3>
                <p className="text-[#6B7280] text-sm leading-relaxed">
                  From model development through validation, monitoring, and retirement. Every stage documented and traceable.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-[#D1D5DB] rounded-xl shadow-sm hover:shadow-lg transition-shadow">
              <CardContent className="p-8">
                <div className="p-3 bg-[#B45309]/10 rounded-lg w-fit mb-4">
                  <FileCheck className="text-[#B45309]" size={28} />
                </div>
                <h3 className="text-xl font-bold text-[#0A1628] mb-3">One-Click Examiner Packs</h3>
                <p className="text-[#6B7280] text-sm leading-relaxed">
                  Automated generation of regulatory examiner documentation — compliant, complete, and consistent every time.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-[#D1D5DB] rounded-xl shadow-sm hover:shadow-lg transition-shadow">
              <CardContent className="p-8">
                <div className="p-3 bg-[#B45309]/10 rounded-lg w-fit mb-4">
                  <Bot className="text-[#B45309]" size={28} />
                </div>
                <h3 className="text-xl font-bold text-[#0A1628] mb-3">Agentic Co-working for Risk</h3>
                <p className="text-[#6B7280] text-sm leading-relaxed">
                  AI co-workers that draft model documentation, flag validation gaps, and surface exceptions — reviewed and approved by your team.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 bg-[#0A1628]">
        <div className="max-w-[1200px] mx-auto text-center">
          <p className="text-[#6B7280] mb-5">
            Learn more about Aureon at
          </p>
          <a 
            href="https://aureon-mrm.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#B45309] font-bold text-2xl hover:underline"
          >
            aureon-mrm.com
            <ExternalLink size={24} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default Aureon;
